"use client";

import { useId, useMemo, useState } from "react";

import { Input } from "@/components/ui/input";
import { SliderRoot, SliderThumb } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

import {
  aggregateByYear,
  buildSchedule,
  loanSummary,
  trueAPR,
} from "./loan-math";
import type { ScheduleRow } from "./loan-math";
import { CURRENCY, DEFAULTS, RANGES, STRINGS } from "./strings";

type TermUnit = "years" | "months";

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: CURRENCY,
  maximumFractionDigits: 0,
});
const grouped = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

const formatUSD = (value: number) => usd.format(Math.max(0, Math.round(value)));
const formatPct = (value: number) =>
  `${(Math.round(value * 10) / 10).toFixed(1)}%`;
const clamp = (value: number, min: number, max: number) =>
  Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : min;

export function BusinessLoanCalculator() {
  const [amount, setAmount] = useState<number>(DEFAULTS.amount);
  const [ratePct, setRatePct] = useState<number>(DEFAULTS.ratePct);
  const [termMonths, setTermMonths] = useState<number>(DEFAULTS.termMonths);
  const [termUnit, setTermUnit] = useState<TermUnit>("years");
  const [feePct, setFeePct] = useState<number>(DEFAULTS.originationFeePct);

  const loan = useMemo(
    () => loanSummary(amount, ratePct, termMonths),
    [amount, ratePct, termMonths]
  );
  const apr = useMemo(
    () => trueAPR(amount, ratePct, termMonths, feePct),
    [amount, ratePct, termMonths, feePct]
  );
  const schedule = useMemo(
    () => buildSchedule(amount, ratePct, termMonths),
    [amount, ratePct, termMonths]
  );

  return (
    <div className="border-grey-100 bg-surface rounded-2xl border p-5 sm:p-7">
      <h3 className="text-heading-16 mb-5 font-semibold text-[#0D0D0D]">
        {STRINGS.toolTitle}
      </h3>

      <div className="grid gap-6 lg:grid-cols-2 lg:gap-10">
        {/* Inputs */}
        <div className="flex flex-col gap-5">
          <MoneyField
            label={STRINGS.loanAmount}
            value={amount}
            range={RANGES.amount}
            onChange={setAmount}
          />
          <PercentField
            label={STRINGS.interestRate}
            value={ratePct}
            range={RANGES.rate}
            onChange={setRatePct}
          />
          <TermField
            value={termMonths}
            unit={termUnit}
            onChangeValue={setTermMonths}
            onChangeUnit={setTermUnit}
          />
          <PercentField
            label={STRINGS.originationFee}
            value={feePct}
            range={RANGES.originationFee}
            onChange={setFeePct}
          />
        </div>

        {/* Outputs */}
        <div
          aria-live="polite"
          className="bg-pulse-50 flex min-h-[18rem] flex-col rounded-xl p-5 sm:p-6"
        >
          <Result
            label={STRINGS.monthlyPayment}
            value={formatUSD(loan.monthlyPayment)}
            emphasis
          />
          <div className="mt-5 flex flex-col gap-3">
            <Result
              label={STRINGS.totalInterest}
              value={formatUSD(loan.totalInterest)}
            />
            <Result
              label={STRINGS.totalRepaid}
              value={formatUSD(loan.totalRepaid)}
            />
            <Result label={STRINGS.trueApr} value={formatPct(apr)} />
          </div>
        </div>
      </div>

      <AmortizationSchedule schedule={schedule} />

      <p className="text-body-sm text-text-label mt-6 italic">
        {STRINGS.disclaimer}
      </p>
    </div>
  );
}

function AmortizationSchedule({ schedule }: { schedule: ScheduleRow[] }) {
  const [open, setOpen] = useState(false);
  const [granularity, setGranularity] = useState<"years" | "months">("years");
  if (schedule.length === 0) return null;

  const rows =
    granularity === "years"
      ? aggregateByYear(schedule).map((y) => ({
          key: y.year,
          label: `Year ${y.year}`,
          principalPaid: y.principalPaid,
          interestPaid: y.interestPaid,
          balance: y.balance,
        }))
      : schedule.map((m) => ({
          key: m.period,
          label: `Month ${m.period}`,
          principalPaid: m.principalPaid,
          interestPaid: m.interestPaid,
          balance: m.balance,
        }));

  return (
    <div className="mt-6">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="bg-pulse-50 text-body-sm hover:bg-pulse-100 focus-visible:ring-pulse-500 flex w-full items-center justify-between gap-2 rounded-lg px-4 py-3 text-left font-semibold text-[#0D0D0D] transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <span>
          {open ? "Hide" : "View"} {STRINGS.scheduleTitle.toLowerCase()}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
          className={cn("size-4 transition-transform", open && "rotate-180")}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <div className="mt-4">
          <div
            role="radiogroup"
            aria-label="Schedule granularity"
            className="bg-grey-50 mb-3 inline-flex rounded-sm p-0.5"
          >
            {(["years", "months"] as const).map((g) => (
              <button
                key={g}
                type="button"
                role="radio"
                aria-checked={granularity === g}
                onClick={() => setGranularity(g)}
                className={cn(
                  "text-body-sm rounded-sm px-3 py-1",
                  granularity === g
                    ? "bg-pulse-700 text-white"
                    : "text-text-label"
                )}
              >
                {g === "years" ? STRINGS.byYears : STRINGS.byMonths}
              </button>
            ))}
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="text-body-sm w-full">
              <thead className="bg-surface text-text-label sticky top-0 text-left">
                <tr>
                  <th className="py-2 pr-3 font-medium">{STRINGS.colPeriod}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {STRINGS.colPrincipal}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {STRINGS.colInterest}
                  </th>
                  <th className="py-2 pl-3 text-right font-medium">
                    {STRINGS.colBalance}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-grey-100 border-t">
                    <td className="py-2 pr-3 text-[#0D0D0D]">{r.label}</td>
                    <td className="px-3 py-2 text-right text-[#0D0D0D]">
                      {formatUSD(r.principalPaid)}
                    </td>
                    <td className="text-text-label px-3 py-2 text-right">
                      {formatUSD(r.interestPaid)}
                    </td>
                    <td className="py-2 pl-3 text-right text-[#0D0D0D]">
                      {formatUSD(r.balance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

interface Range {
  min: number;
  max: number;
  step: number;
}

function FieldShell({
  label,
  htmlFor,
  children,
  control,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
  control: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-3">
        <label
          htmlFor={htmlFor}
          className="text-body-sm font-semibold text-[#0D0D0D]"
        >
          {label}
        </label>
        {control}
      </div>
      {children}
    </div>
  );
}

function RangeSlider({
  value,
  range,
  ariaLabel,
  onChange,
}: {
  value: number;
  range: Range;
  ariaLabel: string;
  onChange: (v: number) => void;
}) {
  return (
    <SliderRoot
      aria-label={ariaLabel}
      min={range.min}
      max={range.max}
      step={range.step}
      value={[value]}
      onValueChange={(vals) =>
        onChange(clamp(vals[0] ?? range.min, range.min, range.max))
      }
      rangeClassName="bg-pulse-700"
      className="py-2"
    >
      <SliderThumb className="bg-pulse-700 focus-visible:ring-pulse-500 size-4 cursor-pointer rounded-full" />
    </SliderRoot>
  );
}

function MoneyField({
  label,
  value,
  range,
  onChange,
}: {
  label: string;
  value: number;
  range: Range;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <FieldShell
      label={label}
      htmlFor={id}
      control={
        <div className="relative">
          <span className="text-text-label pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">
            $
          </span>
          <Input
            id={id}
            inputMode="numeric"
            aria-label={label}
            value={grouped.format(value)}
            onChange={(e) => {
              const next = Number(e.target.value.replace(/[^0-9]/g, ""));
              onChange(clamp(next, range.min, range.max));
            }}
            className="w-36 pl-6 text-right"
          />
        </div>
      }
    >
      <RangeSlider
        value={value}
        range={range}
        ariaLabel={label}
        onChange={onChange}
      />
    </FieldShell>
  );
}

function PercentField({
  label,
  value,
  range,
  onChange,
}: {
  label: string;
  value: number;
  range: Range;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <FieldShell
      label={label}
      htmlFor={id}
      control={
        <div className="relative">
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            aria-label={label}
            min={range.min}
            max={range.max}
            step={range.step}
            value={value}
            onChange={(e) =>
              onChange(clamp(Number(e.target.value), range.min, range.max))
            }
            className="w-24 pr-7 text-right"
          />
          <span className="text-text-label pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">
            %
          </span>
        </div>
      }
    >
      <RangeSlider
        value={value}
        range={range}
        ariaLabel={label}
        onChange={onChange}
      />
    </FieldShell>
  );
}

function TermField({
  value,
  unit,
  onChangeValue,
  onChangeUnit,
}: {
  value: number;
  unit: TermUnit;
  onChangeValue: (months: number) => void;
  onChangeUnit: (unit: TermUnit) => void;
}) {
  const id = useId();
  const range = RANGES.termMonths;
  const displayValue = unit === "years" ? value / 12 : value;
  return (
    <FieldShell
      label={STRINGS.repaymentTerm}
      htmlFor={id}
      control={
        <div className="flex items-center gap-2">
          <Input
            id={id}
            type="number"
            inputMode="decimal"
            aria-label={STRINGS.repaymentTerm}
            value={
              unit === "years"
                ? Math.round(displayValue * 100) / 100
                : displayValue
            }
            step={unit === "years" ? 0.25 : 1}
            onChange={(e) => {
              const raw = Number(e.target.value);
              const months =
                unit === "years" ? Math.round(raw * 12) : Math.round(raw);
              onChangeValue(clamp(months, range.min, range.max));
            }}
            className="w-20 text-right"
          />
          <div
            role="radiogroup"
            aria-label="Term unit"
            className="bg-grey-50 flex rounded-sm p-0.5"
          >
            {(["years", "months"] as TermUnit[]).map((u) => (
              <button
                key={u}
                type="button"
                role="radio"
                aria-checked={unit === u}
                onClick={() => onChangeUnit(u)}
                className={cn(
                  "text-body-sm rounded-sm px-2 py-1 capitalize",
                  unit === u ? "bg-pulse-700 text-white" : "text-text-label"
                )}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
      }
    >
      <RangeSlider
        value={value}
        range={range}
        ariaLabel={STRINGS.repaymentTerm}
        onChange={(months) => onChangeValue(Math.round(months))}
      />
    </FieldShell>
  );
}

function Result({
  label,
  value,
  emphasis,
}: {
  label: string;
  value: string;
  emphasis?: boolean;
}) {
  return (
    <div className={cn(emphasis && "border-grey-100 border-b pb-4")}>
      <p className="text-body-sm text-text-label">{label}</p>
      <p
        className={cn(
          emphasis
            ? "text-heading-32 text-pulse-700"
            : "text-heading-16 font-semibold text-[#0D0D0D]"
        )}
      >
        {value}
      </p>
    </div>
  );
}
